import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Upload,
  RotateCcw,
  Loader2,
  Image as ImageIcon,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { resolveMediaUrl } from "@/hooks/useMarketingImage";
import { formatApiErrorDetail } from "@/lib/apiError";

// Slots displayed in the admin UI — keep in sync with backend whitelist
// (`ALLOWED_MARKETING_KEYS` in routes/admin_marketing.py).
// `default_url` mirrors the hardcoded fallback in the public pages so the
// admin always sees what's currently displayed on the site, even when no
// override has been uploaded yet.
const SLOTS = [
  {
    key: "home.hero",
    label_fr: "Accueil — Bannière principale",
    label_en: "Home — Hero banner",
    description_fr: "La grande image plein écran sous la barre de navigation.",
    description_en: "The full-screen image at the top of the home page.",
    aspect: "aspect-[16/9]",
    default_url: "https://images.pexels.com/photos/35247374/pexels-photo-35247374.jpeg",
  },
  {
    key: "home.studio",
    label_fr: "Accueil & Studio — Section Studio",
    label_en: "Home & Studio — Studio section",
    description_fr: "Image montrée dans la section Studio (accueil + page À propos, haut).",
    description_en: "Shown in the Studio section (home + About page top).",
    aspect: "aspect-[4/5]",
    default_url: "https://images.pexels.com/photos/17345669/pexels-photo-17345669.jpeg",
  },
  {
    key: "about.studio_2",
    label_fr: "Page Studio — Image bas gauche",
    label_en: "Studio page — Bottom-left image",
    description_fr: "Première image de la bande horizontale en bas de la page Studio.",
    description_en: "First image of the horizontal band at the bottom of the Studio page.",
    aspect: "aspect-[4/3]",
    default_url: "https://images.pexels.com/photos/38055414/pexels-photo-38055414.jpeg",
  },
  {
    key: "about.studio_3",
    label_fr: "Page Studio — Image bas droite",
    label_en: "Studio page — Bottom-right image",
    description_fr: "Deuxième image de la bande horizontale en bas de la page Studio.",
    description_en: "Second image of the horizontal band at the bottom of the Studio page.",
    aspect: "aspect-[4/3]",
    default_url: "https://images.pexels.com/photos/35247377/pexels-photo-35247377.jpeg",
  },
];

const MarketingImagesPanel = () => {
  const { authedAxios } = useAuth();
  const { lang } = useLanguage();
  const { refresh: refreshSiteContent, c } = useSiteContent();
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  // Per-slot natural aspect ratio detected once the preview image loads —
  // makes the frame match the actual photo (portrait, landscape, square)
  // instead of forcing the slot's recommended ratio.
  const [slotAspects, setSlotAspects] = useState({});
  const fileInputs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ax = authedAxios();
      const { data } = await ax.get("/admin/marketing-images");
      setOverrides(data.overrides || {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    queueMicrotask(() => load());
  }, [load]);

  const handleUpload = async (key, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(lang === "fr" ? "Le fichier doit être une image." : "File must be an image.");
      return;
    }
    setBusyKey(key);
    // Reset stored aspect for this slot so the new image dictates the frame
    setSlotAspects((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    const form = new FormData();
    form.append("key", key);
    form.append("file", file);
    try {
      const ax = authedAxios();
      await ax.post("/admin/marketing-images/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(lang === "fr" ? "Image mise à jour" : "Image updated");
      await Promise.all([load(), refreshSiteContent()]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setBusyKey(null);
    }
  };

  const handleReset = async (key) => {
    if (!window.confirm(
      lang === "fr"
        ? "Revenir à l'image par défaut pour cet emplacement ?"
        : "Revert this slot to its built-in default image?"
    )) {
      return;
    }
    setBusyKey(key);
    setSlotAspects((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    try {
      const ax = authedAxios();
      await ax.delete(`/admin/marketing-images/${key}`);
      toast.success(lang === "fr" ? "Image réinitialisée" : "Image reset");
      await Promise.all([load(), refreshSiteContent()]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setBusyKey(null);
    }
  };

  const triggerPicker = (key) => {
    const el = fileInputs.current[key];
    if (el) el.click();
  };

  // Live preview: prefer the latest override (resolved through backend URL),
  // otherwise show the built-in default image so the admin always sees
  // what's currently displayed on the live site.
  const previewSrc = (slot) => {
    if (overrides[slot.key]) return resolveMediaUrl(overrides[slot.key]);
    const cmsValue = c(`marketing_images.${slot.key}`, null);
    if (cmsValue) return resolveMediaUrl(cmsValue);
    return slot.default_url;
  };

  return (
    <section data-testid="admin-marketing-panel" className="space-y-10">
      <div>
        <p className="overline kw-accent-line">
          {lang === "fr" ? "Identité visuelle" : "Visual identity"}
        </p>
        <h2 className="font-serif-display text-4xl md:text-5xl mt-4">
          {lang === "fr" ? "Images marketing" : "Marketing images"}
        </h2>
        <p className="mt-3 text-sm text-[#A19E9C] max-w-2xl leading-relaxed">
          {lang === "fr"
            ? "Les photos éditoriales qui apparaissent en dehors du portfolio (bannière accueil, section studio, page À propos). Pour chaque emplacement, télécharge ta propre image ou reviens à la photo par défaut."
            : "The editorial photos shown outside the portfolio (home banner, studio section, About page). Upload your own image for each slot, or revert to the built-in default."}
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-3 text-[#A19E9C] py-12">
          <Loader2 className="w-4 h-4 animate-spin text-[#C5A059]" strokeWidth={1.5} />
          <span className="text-xs uppercase tracking-[0.3em]">
            {lang === "fr" ? "Chargement…" : "Loading…"}
          </span>
        </div>
      )}

      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SLOTS.map((slot) => {
            const customized = Boolean(overrides[slot.key]);
            const isBusy = busyKey === slot.key;
            const src = previewSrc(slot);
            return (
              <motion.div
                key={slot.key}
                data-testid={`marketing-slot-${slot.key.replace(/\./g, "-")}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="border border-[#F2EBE5]/10 bg-[#0a0a0a] p-5 flex flex-col gap-4"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif-display text-xl text-[#F2EBE5]">
                      {lang === "fr" ? slot.label_fr : slot.label_en}
                    </h3>
                    <p className="text-xs text-[#A19E9C] mt-1 leading-relaxed">
                      {lang === "fr" ? slot.description_fr : slot.description_en}
                    </p>
                  </div>
                  <span
                    className="text-[9px] uppercase tracking-[0.25em] px-2 py-1 border whitespace-nowrap flex items-center gap-1"
                    style={{
                      borderColor: customized ? "rgba(197,160,89,0.45)" : "rgba(161,158,156,0.25)",
                      color: customized ? "#C5A059" : "#A19E9C",
                    }}
                  >
                    {customized ? (
                      <>
                        <CheckCircle2 className="w-3 h-3" strokeWidth={1.5} />
                        {lang === "fr" ? "Personnalisée" : "Custom"}
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-3 h-3" strokeWidth={1.5} />
                        {lang === "fr" ? "Par défaut" : "Default"}
                      </>
                    )}
                  </span>
                </div>

                {/* Preview — adapts to the actual image format (portrait,
                    landscape, square). Falls back to the slot's recommended
                    aspect until the image's natural dimensions are known. */}
                <div
                  className={`${slotAspects[slot.key] ? "" : slot.aspect} bg-[#050505] border border-[#F2EBE5]/5 overflow-hidden relative transition-[aspect-ratio] duration-300`}
                  style={slotAspects[slot.key] ? { aspectRatio: slotAspects[slot.key] } : undefined}
                >
                  {src ? (
                    <img
                      src={src}
                      alt={slot.label_en}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      onLoad={(e) => {
                        const w = e.currentTarget.naturalWidth;
                        const h = e.currentTarget.naturalHeight;
                        if (w > 0 && h > 0) {
                          setSlotAspects((prev) => {
                            if (prev[slot.key] === w / h) return prev;
                            return { ...prev, [slot.key]: w / h };
                          });
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-[#A19E9C]/40" strokeWidth={1} />
                    </div>
                  )}
                  {isBusy && (
                    <div className="absolute inset-0 bg-[#050505]/70 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" strokeWidth={1.5} />
                    </div>
                  )}
                </div>

                {/* Hidden file input */}
                <input
                  ref={(el) => {
                    fileInputs.current[slot.key] = el;
                  }}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid={`marketing-file-input-${slot.key.replace(/\./g, "-")}`}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = ""; // reset so the same file can be re-selected
                    handleUpload(slot.key, file);
                  }}
                />

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => triggerPicker(slot.key)}
                    disabled={isBusy}
                    data-testid={`marketing-upload-btn-${slot.key.replace(/\./g, "-")}`}
                    className="flex-1 kw-btn-ghost"
                  >
                    <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {lang === "fr" ? "Changer la photo" : "Change photo"}
                  </button>
                  {customized && (
                    <button
                      type="button"
                      onClick={() => handleReset(slot.key)}
                      disabled={isBusy}
                      data-testid={`marketing-reset-btn-${slot.key.replace(/\./g, "-")}`}
                      className="px-3 py-2.5 border border-[#F2EBE5]/15 text-[#A19E9C] hover:text-[#E27474] hover:border-[#E27474]/40 transition-colors text-[10px] uppercase tracking-[0.25em] flex items-center gap-1.5"
                      title={lang === "fr" ? "Revenir à l'image par défaut" : "Revert to default"}
                    >
                      <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {lang === "fr" ? "Défaut" : "Default"}
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Tip */}
      <div className="border-l-2 border-[#C5A059]/40 bg-[#0a0a0a] p-5 text-xs text-[#A19E9C] leading-relaxed">
        <p>
          <strong className="text-[#C5A059] font-normal">
            {lang === "fr" ? "Conseils : " : "Tips: "}
          </strong>
          {lang === "fr"
            ? "Préfère des images haute résolution (≥ 1920px) en JPG ou WebP. Pour la bannière d'accueil, le format paysage 16/9 fonctionne le mieux. Pour les images studio, des portraits 4/5 ou 4/3 produisent un meilleur rendu."
            : "Use high-resolution images (≥ 1920px) in JPG or WebP. Landscape 16/9 works best for the hero banner. Studio images look best in 4/5 portrait or 4/3 format."}
        </p>
      </div>
    </section>
  );
};

export default MarketingImagesPanel;
